/**
 * One boat advancing along an intended path until the map stops it: the shore
 * clip, the crash speed, and the bounce chain, in the order `planMove` has
 * always run them.
 *
 * This used to live inline in `planMove`, which was fine while the only thing
 * that moved a boat was its own click. A boat shoved by another boat has to
 * meet the same shore on the same terms, so the step is shared rather than
 * reimplemented next to the collision solver.
 */
import { resolveBounceChain } from './bounce.ts';
import {
  classifyDestination,
  clipPathToSolid,
  pointInSolid,
  type CollisionContact,
} from './collision.ts';
import { CRASH_HARSHNESS, MIN_SPEED, TURN_TAX } from './constants.ts';
import type { CrashImpact } from './crash.ts';
import {
  applyCrashCost,
  driveLevel,
  isScrape,
  SCRAPE_SPEED_KEEP,
  scrapeDrive,
} from './crashCost.ts';
import type { MapDocument } from './map/types.ts';
import { angleDelta, crossingAngleDeg, length, sub, toAngle } from './math.ts';
import { turnKeptSpeed } from './movement.ts';
import { sampleMovePath, type MovePath } from './path.ts';
import type { BoatState, Vec2 } from './types.ts';

export type TerrainRun = {
  /** Where the boat ends up, after any bounce. */
  boat: BoatState;
  /** The full swim: inbound clip, fillets, and outbound casts. */
  path: MovePath | null;
  /** The intended path up to the first solid, so chrome can point at the wall. */
  impactPath: MovePath | null;
  clipped: boolean;
  crash: CrashImpact | null;
  crossingDeg: number | null;
  /** Contacts the move has to obey; anything past the first solid is dropped. */
  contacts: CollisionContact[];
  /**
   * Contacts worth logging. A boat escaping land keeps the solid it started
   * inside on the log while the move itself ignores it, which is why this is
   * not the same list as `contacts`.
   */
  reported: CollisionContact[];
};

/**
 * Walk `intended` until terrain interrupts it. `landedIfClear` is where the boat
 * ends up when nothing does — the caller already solved that, because a click
 * lands on the point clicked and a shove lands at the end of its cast.
 *
 * Returns null only when the path cannot be walked at all (a boat that starts
 * and ends inside land), which is the same refusal `planMove` has always made.
 */
export function advanceThroughTerrain(
  map: MapDocument,
  boat: BoatState,
  intended: MovePath | null,
  landedIfClear: BoatState,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
): TerrainRun | null {
  const destination = intended?.segments.at(-1)?.p3 ?? landedIfClear.position;
  const collisions = classifyDestination(map, boat.position, destination, intended);
  const startInSolid = pointInSolid(map, boat.position);

  if (startInSolid && pointInSolid(map, destination)) {
    return null;
  }

  if (!collisions.blocked || startInSolid) {
    return {
      boat: landedIfClear,
      path: intended,
      impactPath: null,
      clipped: false,
      crash: null,
      crossingDeg: null,
      contacts: collisions.contacts.filter((contact) => !contact.solid),
      reported: collisions.contacts,
    };
  }

  const clip = intended ? clipPathToSolid(map, intended, boat.position) : null;

  if (!clip) {
    return null;
  }

  const solidT = earliestSolidT(collisions.contacts);
  const landed = landAlongPath(boat, clip.path, clip.point, turnTax);
  const crossing = crossingAngleDeg(clip.approach, sub(clip.edge.to, clip.edge.from));
  const bounced = resolveBounceChain(map, clip, landed.speed, crashHarshness);
  const kept =
    solidT === null
      ? collisions.contacts
      : collisions.contacts.filter((contact) => contact.t <= solidT);

  return {
    boat: settleHit(boat, bounced.boat, crossing, crashHarshness),
    path: bounced.path,
    impactPath: clip.path,
    clipped: true,
    crash: crossing === null ? null : { impact: landed.speed, crossingDeg: crossing },
    crossingDeg: crossing,
    contacts: kept,
    reported: kept,
  };
}

/**
 * A scrape keeps most of the shore-curve speed and drops one circle size.
 * A harder hit stops the boat and opens the circle from the small disk again.
 * Harshness 0 keeps the clipped travel and the pace the boat already had.
 */
function settleHit(
  from: BoatState,
  landed: BoatState,
  crossing: number | null,
  harshness: number,
): BoatState {
  if (harshness <= 0) {
    return { ...landed, drive: driveLevel(from) };
  }

  if (isScrape(crossing)) {
    return {
      ...landed,
      speed: Math.max(0, landed.speed * SCRAPE_SPEED_KEEP),
      drive: scrapeDrive(from),
    };
  }

  return {
    ...landed,
    speed: applyCrashCost(landed.speed),
    drive: 0,
  };
}

/** Speed and heading at the end of a clipped run, before the bounce spends them. */
export function landAlongPath(
  boat: BoatState,
  path: MovePath,
  end: Vec2,
  tax: number = TURN_TAX,
): BoatState {
  const samples = sampleMovePath(path);
  const last = samples.at(-1) ?? end;
  const previous = samples.at(-2);
  const heading =
    previous && length(sub(last, previous)) > 1e-9
      ? toAngle(sub(last, previous))
      : boat.heading;
  const chord = sub(end, boat.position);
  const distance = length(chord);
  const offset = distance === 0 ? 0 : angleDelta(boat.heading, toAngle(chord));

  return {
    position: { x: end.x, y: end.y },
    heading,
    speed: Math.max(MIN_SPEED, turnKeptSpeed(distance, offset, tax)),
  };
}

function earliestSolidT(contacts: readonly CollisionContact[]): number | null {
  let earliest: number | null = null;

  for (const contact of contacts) {
    if (!contact.solid) {
      continue;
    }

    if (earliest === null || contact.t < earliest) {
      earliest = contact.t;
    }
  }

  return earliest;
}
